import Formsy from 'formsy-react'

import cn from 'classnames'

import Select from '@components/Select/RequiredSelectWithFormsy'
import FormsyInput from '@components/Textfield/withFormsy'
import Button from '@components/Button'
import ErrorMessage from '@components/ErrorMessage'

import { getYearsSelection, getMonthsSelection } from '../../utils'
import { useConfirm } from 'shared/hooks/useConfirm'
import isMobile from 'shared/responsive-helpers'
import useRequestPending from 'shared/hooks/useRequestPending'

import styles from './styles.module.scss'

const creditCardInputHelper = value =>
    value
        .replace(/\W/gi, '')
        .replace(/(.{4})/g, '$1 ')
        .trim()

const CreditCardForm = ({ onSubmit, zip, pending, translate: t, successCallback, shouldShowInfo = true }) => {
    const formRef = React.useRef(null)
    const { confirm, ConfirmModal } = useConfirm()
    const { isLoading, error } = useRequestPending(pending, () => {
        formRef && formRef.current.reset()
        if (successCallback) successCallback()
    })

    const buttonClassName = cn({
        'mb-5': zip,
    })

    const submit = async model => {
        if (shouldShowInfo) {
            const isConfirmed = await confirm()
            if (!isConfirmed) {
                return
            }
        }

        const { cardholdersName, cardNumber, expiryMonth, expiryYear, zipCode } = model
        onSubmit(cardholdersName, cardNumber, expiryMonth, expiryYear, zip ? zip : zipCode)
    }

    return (
        <>
            <ConfirmModal title="credit.card.confirm.title" text="credit.card.confirm.text" />
            <Formsy onSubmit={submit} ref={formRef}>
                <FormsyInput
                    placeholder={t('sixcards.creditcard.cardholders-name.label')}
                    name="cardholdersName"
                    validations={{ isSpecialWords: true, maxLength: 40 }}
                    validationError={t('sixcards.creditcard.cardholders-name.error')}
                    required
                    avoidErrorOnReset
                    value=""
                />
                <FormsyInput
                    placeholder={t('sixcards.creditcard.cardnumber.label')}
                    name="cardNumber"
                    type={isMobile() ? 'tel' : 'text'}
                    validations={{ matchRegexp: /^[0-9]/, minLength: 12, maxLength: 23 }}
                    validationError={t('sixcards.creditcard.cardnumber.error')}
                    valueModifier={!isMobile() && creditCardInputHelper} // don't use on mobile, because some android + chrome can't handle it
                    required
                    value=""
                    avoidErrorOnReset
                />
                <div className="d-flex justify-content-between">
                    <div className="d-flex col px-0">
                        <div className={styles.monthInput}>
                            <Select
                                placeholder={t('sixcards.creditcard.expiry-month.label')}
                                name="expiryMonth"
                                options={getMonthsSelection()}
                                getSelectValue={v => getMonthsSelection().find(c => c.value === v) || ''}
                                required
                                value=""
                            />
                        </div>
                        <div className={styles.yearInput}>
                            <Select
                                placeholder={t('sixcards.creditcard.expiry-year.label')}
                                name="expiryYear"
                                options={getYearsSelection()}
                                getSelectValue={v => getYearsSelection().find(c => c.value === v) || ''}
                                required
                                value=""
                            />
                        </div>
                    </div>
                    <FormsyInput
                        className={styles.ccv}
                        placeholder={t('sixcards.creditcard.ccv.label')}
                        name="ccv"
                        validations={{ isNumeric: true, minLength: 3 }}
                        validationError={t('sixcards.creditcard.ccv.error')}
                        required
                        value=""
                        avoidErrorOnReset
                    />
                </div>
                {!zip && (
                    <FormsyInput
                        className={styles.zip}
                        placeholder={t('sixcards.creditcard.zip-code.label')}
                        name="zipCode"
                        validations={{ isAlphanumeric: true }}
                        validationError={t('sixcards.creditcard.zip-code.error')}
                        required
                        value=""
                        avoidErrorOnReset
                    />
                )}

                <Button type="submit" isLoading={isLoading} disabled={isLoading} className={buttonClassName}>
                    {t('sixcards.creditcard.submit.label')}
                </Button>

                {error && <ErrorMessage messages={[error]} className="mt-3" />}
            </Formsy>
        </>
    )
}

export default withTranslate(CreditCardForm)
